/**
 * NotificationCenter - In-app notification bell and dropdown
 * Shows unread count badge and list of recent notifications
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Bell, 
  BellRing,
  Check,
  CheckCheck,
  X,
  Calendar,
  AlertTriangle,
  PoundSterling,
  FileText,
  Clock,
  Settings,
  Sparkles
} from 'lucide-react';
import { pushNotificationsAPI } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Notification type icons
const TYPE_ICONS = {
  shift_assigned: Calendar,
  shift_confirmed: Check,
  shift_cancelled: X,
  shift_updated: Calendar,
  shift_reminder: Clock,
  compliance_expiring: AlertTriangle,
  compliance_expired: AlertTriangle,
  expense_approved: PoundSterling,
  expense_rejected: PoundSterling,
  document_approved: FileText,
  document_rejected: FileText,
  trial_reminder: Sparkles,
  general: Bell,
};

// Notification type colors
const TYPE_COLORS = {
  shift_assigned: 'text-[#1ABC9C]',
  shift_confirmed: 'text-[#1ABC9C]',
  shift_cancelled: 'text-red-400',
  shift_updated: 'text-[#FFC04D]',
  shift_reminder: 'text-[#FFC04D]',
  compliance_expiring: 'text-[#FFC04D]',
  compliance_expired: 'text-red-400',
  expense_approved: 'text-[#1ABC9C]',
  expense_rejected: 'text-red-400',
  document_approved: 'text-[#1ABC9C]',
  document_rejected: 'text-red-400',
  trial_reminder: 'text-[#1ABC9C]',
  general: 'text-[#A1A1AA]',
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await pushNotificationsAPI.getInApp();
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await pushNotificationsAPI.getUnreadCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await pushNotificationsAPI.markRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await pushNotificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navigate based on notification type and data
    const { type, data } = notification;
    
    if (type.startsWith('shift_') && data?.shift_id) {
      navigate(`/portal/shifts/${data.shift_id}`);
    } else if (type.startsWith('expense_') && data?.expense_id) {
      navigate('/portal/expenses');
    } else if (type.startsWith('compliance_') || type.startsWith('document_')) {
      navigate('/portal/documents');
    } else if (type === 'trial_reminder' || data?.action === 'open_upgrade_dialog') {
      // Trial reminders open the upgrade/lead dialog. The dialog lives
      // inside TrialBanner (mounted in Layout), so emit a window event
      // it can subscribe to; non-admin recipients still land on the
      // dashboard where the trial banner is visible.
      window.dispatchEvent(new CustomEvent('elara:open-upgrade-dialog'));
      navigate('/');
    }
    
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-[#A1A1AA] hover:text-[#F2F2F2]"
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
        {unreadCount > 0 && (
          <Badge 
            className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center bg-red-500 text-white text-xs border-0 p-0"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Dropdown — on mobile, anchor to the viewport (`fixed`) because the
          bell sits in the middle of the header; anchoring `right-0` to the
          bell itself pushed the panel off-screen to the left (iter71.4). */}
      {isOpen && (
        <div 
          className="fixed top-14 right-2 left-2 sm:left-auto sm:absolute sm:top-auto sm:right-0 sm:mt-2 sm:w-96 bg-[#2B2B2B] border border-white/10 rounded-lg shadow-xl z-50 max-h-[80vh] flex flex-col"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <h3 className="font-semibold text-[#F2F2F2]">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-[#1ABC9C] hover:text-[#1ABC9C]/80 h-7"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  navigate('/portal/profile');
                  setIsOpen(false);
                }}
                className="h-7 w-7 text-[#A1A1AA] hover:text-[#F2F2F2]"
                title="Notification settings"
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Notification List */}
          <ScrollArea className="max-h-[400px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 mx-auto mb-2 text-[#A1A1AA]/50" />
                <p className="text-[#A1A1AA] text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {notifications.map(notification => {
                  const IconComponent = TYPE_ICONS[notification.type] || Bell;
                  const iconColor = TYPE_COLORS[notification.type] || 'text-[#A1A1AA]';
                  const isTrialReminder = notification.type === 'trial_reminder';
                  const daysLeft = notification.data?.days_remaining;
                  const daysBadge = isTrialReminder && typeof daysLeft === 'number' ? (
                    daysLeft <= 0
                      ? { label: 'Ends today', cls: 'bg-red-500/15 text-red-300 border-red-500/40' }
                      : daysLeft === 1
                        ? { label: '1 day left', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' }
                        : { label: `${daysLeft} days left`, cls: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/40' }
                  ) : null;

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`
                        p-3 cursor-pointer transition-colors
                        ${notification.read ? 'bg-transparent' : 'bg-[#1ABC9C]/5'}
                        hover:bg-white/5
                      `}
                      data-testid={`notification-item-${notification.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${notification.read ? 'text-[#A1A1AA]' : 'text-[#F2F2F2]'}`}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <div className="w-2 h-2 rounded-full bg-[#1ABC9C] flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          <p className="text-xs text-[#A1A1AA] mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-[#A1A1AA]/60">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </p>
                            {daysBadge && (
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${daysBadge.cls}`}
                                data-testid={`trial-days-badge-${notification.id}`}
                              >
                                {daysBadge.label}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-white/10">
              <Button
                variant="ghost"
                className="w-full text-xs text-[#A1A1AA] hover:text-[#F2F2F2]"
                onClick={() => {
                  // Could navigate to a full notifications page if needed
                  setIsOpen(false);
                }}
              >
                View all notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
